import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

interface Defect {
  id: string;
  category: string;
  severity: string;
  description: string;
  photos: string[];
  status: string;
  reported_at: string;
  vehicle_registration?: string;
  driver_name?: string;
}

const Defects: React.FC = () => {
  const [defects, setDefects] = useState<Defect[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDefect, setSelectedDefect] = useState<Defect | null>(null);

  useEffect(() => {
    fetchDefects();
  }, []);

  const fetchDefects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/defects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefects(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching defects:', error);
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_BASE}/defects/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDefects();
      if (selectedDefect?.id === id) {
        setSelectedDefect({ ...selectedDefect, status });
      }
    } catch (error) {
      console.error('Error updating defect status:', error);
      alert('Failed to update defect status');
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Defects</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {defects.map((defect) => (
            <div
              key={defect.id}
              onClick={() => setSelectedDefect(defect)}
              className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition ${
                selectedDefect?.id === defect.id ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    defect.severity === 'critical' ? 'bg-red-100 text-red-800' :
                    defect.severity === 'major' ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {defect.severity}
                  </span>
                  <span className="ml-2 text-sm text-gray-600">{defect.category}</span>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${
                  defect.status === 'resolved' ? 'bg-green-100 text-green-800' :
                  defect.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  defect.status === 'acknowledged' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {defect.status}
                </span>
              </div>
              <p className="text-sm mb-2">{defect.description}</p>
              <div className="flex justify-between text-xs text-gray-500">
                <span>{new Date(defect.reported_at).toLocaleString()}</span>
                <span>{defect.photos.length} photo(s)</span>
              </div>
            </div>
          ))}
        </div>

        {selectedDefect && (
          <div className="bg-white rounded-lg shadow p-6 sticky top-6">
            <h2 className="text-xl font-bold mb-4">Defect Details</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <p className="text-lg capitalize">{selectedDefect.category}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <span className={`px-3 py-1 rounded ${
                selectedDefect.severity === 'critical' ? 'bg-red-100 text-red-800' :
                selectedDefect.severity === 'major' ? 'bg-orange-100 text-orange-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {selectedDefect.severity}
              </span>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <p>{selectedDefect.description}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Photos</label>
              <div className="grid grid-cols-2 gap-2">
                {selectedDefect.photos.map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`Defect ${index + 1}`}
                    className="w-full h-32 object-cover rounded"
                  />
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reported</label>
              <p className="text-sm">{new Date(selectedDefect.reported_at).toLocaleString()}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Update Status</label>
              <div className="flex gap-2">
                <button
                  onClick={() => updateStatus(selectedDefect.id, 'acknowledged')}
                  className="flex-1 px-3 py-2 text-sm bg-purple-100 text-purple-800 rounded hover:bg-purple-200"
                >
                  Acknowledged
                </button>
                <button
                  onClick={() => updateStatus(selectedDefect.id, 'in_progress')}
                  className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                >
                  In Progress
                </button>
                <button
                  onClick={() => updateStatus(selectedDefect.id, 'resolved')}
                  className="flex-1 px-3 py-2 text-sm bg-green-100 text-green-800 rounded hover:bg-green-200"
                >
                  Resolved
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Defects;
